/**
 * historyStack.ts — React hook for navigation history + cycle-guard counter.
 *
 * Key invariants (spec §2):
 *   - history: push on every forward transition, pop on Back.
 *   - RESTART: clears `history` array but does NOT reset `transitions` counter.
 *     (Anti-pattern #6: resetting counter allows infinite RESTART loops.)
 *   - cycle-guard: transitions > maxTransitions = max(screens.length * 3, 30)
 *     → guard trips. Checked BEFORE each forward transition in the player.
 *   - cycle_guard_tripped fires once per mount (one-shot guard in player).
 *
 * Uses `historyRef` for synchronous reads/writes (pop must return synchronously)
 * and `setVersion` to trigger React re-renders when the history changes.
 */

import { useCallback, useRef, useState } from 'react';
import type { EditorScreen } from '@/store/editorSlice';

export interface HistoryStack {
  /** Current history stack (screen indices visited before current). */
  readonly history: number[];
  /** Push the current screen index BEFORE navigating forward. */
  push: (index: number) => void;
  /**
   * Pop the last index (used by Back).
   * Returns the index to navigate to, or undefined when history is empty.
   * Synchronous — safe to call and immediately use the returned value.
   */
  pop: () => number | undefined;
  /**
   * Clear history (RESTART). Does NOT reset the transitions counter.
   * Anti-pattern #6 prevention: resetting counter allows infinite RESTART.
   */
  clearHistory: () => void;
  /** Whether the transitions counter exceeds maxTransitions. */
  isGuardTripped: () => boolean;
  /** Current transition count (exposed for telemetry / tests). */
  readonly transitionCount: number;
}

/**
 * Жалоба покупателя 18.09 (funnel.quizsomaapp.info): после перезагрузки внутри
 * квиза кнопка «Назад» пропадала — прогресс экрана восстанавливался
 * (WEB-2177), а история переходов начиналась с нуля, и визард считал экран
 * первым (`isFirst = history.length === 0`).
 *
 * Восстановленную историю нельзя брать на веру: это запись из localStorage
 * покупателя, она могла быть сделана до републикации или испорчена. Поэтому
 * оставляем только целые номера в пределах состава воронки — всё остальное
 * молча отбрасываем, как и везде в этом контуре (fail-open, поток покупателя
 * ломать нельзя).
 */
function sanitizeInitialHistory(
  initial: readonly number[] | undefined,
  screenCount: number,
): number[] {
  if (!initial) return [];
  return initial.filter(
    (index) =>
      Number.isInteger(index) && index >= 0 && index < screenCount,
  );
}

/**
 * @param initialHistory — история переходов из сохранённого прогресса
 * покупателя (quizProgressStorage.ts). Читается только при первом рендере.
 */
export function useHistoryStack(
  screens: EditorScreen[],
  initialHistory?: readonly number[],
): HistoryStack {
  const maxTransitions = Math.max(screens.length * 3, 30);
  // historyRef = synchronous source of truth; setVersion = re-render trigger.
  // Начальное значение читается ОДИН раз на инстанс (useRef-инициализатор):
  // дальше правда живёт в самом стеке, и новая ссылка на тот же массив из
  // хранилища не должна переписывать уже пройденное покупателем.
  const historyRef = useRef<number[]>(
    sanitizeInitialHistory(initialHistory, screens.length),
  );
  // Счётчик переходов стартует с ГЛУБИНЫ восстановленной истории. Это не
  // полное число сделанных человеком переходов: каждое «Назад» укорачивает
  // историю, и после перезагрузки счётчик окажется меньше настоящего. Но без
  // этого он обнулялся бы полностью, а защита от зацикливания
  // (`maxTransitions`) — это потолок, ниже которого занижение лишь делает её
  // мягче, а не отключает. Хранить отдельное число переходов ради точности
  // значило бы завести в браузере покупателя ещё одно поле ради защиты,
  // которая и так срабатывает от тридцати переходов.
  const transitionsRef = useRef(historyRef.current.length);
  const [, setVersion] = useState(0);

  const push = useCallback((index: number) => {
    transitionsRef.current += 1;
    historyRef.current = [...historyRef.current, index];
    setVersion((v) => v + 1);
  }, []);

  const pop = useCallback((): number | undefined => {
    const arr = historyRef.current;
    if (arr.length === 0) return undefined;
    const popped = arr[arr.length - 1];
    historyRef.current = arr.slice(0, -1);
    setVersion((v) => v + 1);
    return popped;
  }, []);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    setVersion((v) => v + 1);
    // IMPORTANT: transitionsRef.current is NOT reset here.
    // Resetting transitions on RESTART would allow infinite RESTART loops (anti-pattern #6).
  }, []);

  const isGuardTripped = useCallback(() => {
    return transitionsRef.current > maxTransitions;
  }, [maxTransitions]);

  return {
    get history() {
      return historyRef.current;
    },
    push,
    pop,
    clearHistory,
    isGuardTripped,
    get transitionCount() {
      return transitionsRef.current;
    },
  };
}
